import React, { useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Animated,
  Platform,
  TouchableOpacity,
} from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  Paprika,
  PaprikaTypography,
  PaprikaSpacing,
  PaprikaRadius,
  PaprikaShadows,
} from '../../styles/theme';

export type ToastType = 'success' | 'error' | 'info';

interface ToastProps {
  visible: boolean;
  message: string;
  type?: ToastType;
  duration?: number;
  onDismiss: () => void;
  /** Optional inline action (e.g. "Undo"). When present, a trailing pill button is rendered. */
  actionLabel?: string;
  onAction?: () => void;
}

const TOAST_CONFIG: Record<ToastType, { bg: string; border: string; icon: string }> = {
  success: {
    bg: '#E8F5E9',
    border: Paprika.success,
    icon: 'check-circle',
  },
  error: {
    bg: '#FFEBEE',
    border: '#F44336',
    icon: 'alert-circle',
  },
  info: {
    bg: Paprika.accentLight,
    border: Paprika.accent,
    icon: 'info',
  },
};

export const Toast: React.FC<ToastProps> = ({
  visible,
  message,
  type = 'success',
  duration,
  onDismiss,
  actionLabel,
  onAction,
}) => {
  const insets = useSafeAreaInsets();
  // Give the user more time to react when an action (e.g. Undo) is offered.
  const effectiveDuration = duration ?? (onAction ? 4200 : 3000);
  const translateY = useRef(new Animated.Value(100)).current;
  const opacity = useRef(new Animated.Value(0)).current;
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    if (!visible) return;

    // Slide in
    Animated.parallel([
      Animated.timing(translateY, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.timing(opacity, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start();

    // Slide out after duration
    const slideOutTimer = setTimeout(() => {
      Animated.parallel([
        Animated.timing(translateY, {
          toValue: 100,
          duration: 250,
          useNativeDriver: true,
        }),
        Animated.timing(opacity, {
          toValue: 0,
          duration: 250,
          useNativeDriver: true,
        }),
      ]).start();
    }, effectiveDuration);

    // Dismiss state after slide-out completes (timeout-based, not animation callback)
    const dismissTimer = setTimeout(() => {
      onDismissRef.current();
    }, effectiveDuration + 300);

    return () => {
      clearTimeout(slideOutTimer);
      clearTimeout(dismissTimer);
    };
  }, [visible, effectiveDuration]);

  if (!visible) return null;

  const config = TOAST_CONFIG[type];

  return (
    <Animated.View
      style={[
        styles.container,
        {
          bottom: Math.max(insets.bottom, PaprikaSpacing.lg) + PaprikaSpacing.md,
          backgroundColor: config.bg,
          borderLeftColor: config.border,
          transform: [{ translateY }],
          opacity,
        },
      ]}
    >
      <Feather
        name={config.icon as any}
        size={20}
        color={config.border}
        style={styles.icon}
      />
      <Text style={styles.message} testID="toast-message">{message}</Text>
      {!!actionLabel && onAction && (
        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => {
            onAction();
            onDismiss();
          }}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          testID="toast-action"
        >
          <Text style={[styles.actionLabel, { color: config.border }]}>{actionLabel}</Text>
        </TouchableOpacity>
      )}
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    left: PaprikaSpacing.lg,
    right: PaprikaSpacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: PaprikaSpacing.md,
    paddingHorizontal: PaprikaSpacing.md,
    borderRadius: PaprikaRadius.md,
    borderLeftWidth: 4,
    ...Platform.select({
      ios: {
        shadowColor: '#52443c',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.15,
        shadowRadius: 4,
      },
      android: {
        elevation: 4,
      },
      web: {
        boxShadow: '0 2px 4px rgba(82,68,60,0.08)',
      },
    }),
  },
  icon: {
    marginRight: PaprikaSpacing.sm,
  },
  message: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textPrimary,
    flex: 1,
  },
  actionButton: {
    marginLeft: PaprikaSpacing.sm,
    paddingHorizontal: PaprikaSpacing.sm,
    paddingVertical: PaprikaSpacing.xs,
  },
  actionLabel: {
    ...PaprikaTypography.labelMedium,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
});

export default Toast;
